// Bibliotheek (build 2.5): losse lessen en oefeningen.
//
// Bronnen:
//   - Seizoensplan: losse lessen die een editor/creator bewust vanuit een seizoensplan
//     heeft gekopieerd (⋯ → "Kopiëren naar losse lessen" op het beginscherm). Ze staan
//     centraal bij de plannen-club in clubs/{planClubId}/official/{itemId}, zodat elke
//     club dezelfde ziet:
//       { kind: "lesson", color, name, sub, theme, adjustments, tags, parts,
//         fromPlan: { id, label, week, les }, copiedAt }
//     Alle accounts lezen; editors en hoger schrijven.
//   - Club / Mijn: eigen lessen en oefeningen van trainers. Elk item is een los
//     document in clubs/{clubId}/library/{itemId}:
//       { kind: "lesson" | "drill", ownerUid, ownerName, shared, name, tags,
//         createdAt, updatedAt,
//         lesson: color ("rood" | "oranje" | "groen" | "geel" | "volwassenen"),
//                 parts: { warmup, exercise1, exercise2, match } (zelfde vorm als een weekles)
//         drill:  description, extraInfo, photo, diagram }
//     Privé (shared: false) ziet alleen de maker; gedeeld ziet de hele club. Zie
//     firestore.rules (match /clubs/{clubId}/library/{itemId}).
import { db } from "../firebase";
import { collection, doc, getDocs, query, where, setDoc, deleteDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { COLORS } from "./constants";
import { lessonBlocks, blocksMinutes } from "./blocks";
import { mkLes, cp } from "./data";

// Vaste kleuren om op te filteren. Seizoensplannen hebben namen als "Rood winter '26"
// of "2e geel"; die worden via colorKeyOf() op een van deze vijf gezet.
export const BASE_COLORS = [
  { key: "rood", label: "Rood", ...COLORS[0] },
  { key: "oranje", label: "Oranje", ...COLORS[1] },
  { key: "groen", label: "Groen", ...COLORS[2] },
  { key: "geel", label: "Geel", ...COLORS[3] },
  { key: "volwassenen", label: "Volwassenen", ...COLORS[4] },
];

// Kleuren die alleen in oude (gearchiveerde) plannen voorkomen.
export const EXTRA_COLORS = [
  { key: "blauw", label: "Blauw", ...COLORS[4] },
];

// `overrides`: de echte kleuren van de huidige seizoensplannen ({ rood: {...}, ... }),
// zodat een club die Volwassenen bv. paars heeft gemaakt ook paars ziet.
export function baseColor(key, overrides) {
  var o = overrides && overrides[key];
  var b = BASE_COLORS.concat(EXTRA_COLORS).find(function (c) { return c.key === key; }) || BASE_COLORS[0];
  return o ? Object.assign({}, b, { color: o.color, on: o.on, ink: o.ink, bg: o.bg, border: o.border }) : b;
}

// Per kleurnaam de kleuren van het eerste actieve plan met die naam.
export function planColorMap(levels) {
  var map = {};
  (levels || []).forEach(function (l) {
    if (l.type === "drills" || l.archived || l.concept) return;
    var k = colorKeyOf(l);
    if (k && !map[k]) map[k] = l;
  });
  return map;
}

export function colorKeyOf(level) {
  if (!level) return null;
  var s = String(level.label || "").toLowerCase();
  var all = BASE_COLORS.concat(EXTRA_COLORS);
  for (var i = 0; i < all.length; i++) {
    if (s.indexOf(all[i].key) >= 0) return all[i].key;
  }
  if (/volw/.test(s)) return "volwassenen";
  // Alleen op kleur gokken voor de vier jeugdkleuren (blauw/paars wisselen per club).
  var hex = String(level.color || "").toUpperCase();
  var idx = COLORS.findIndex(function (c) { return c.color === hex; });
  if (idx >= 0 && idx < 4) return BASE_COLORS[idx].key;
  return null;
}

function hasText(ex) { return !!(ex && ((ex.description && ex.description.trim()) || (ex.name && ex.name.trim()) || (ex.accents && ex.accents.length))); }
export function lessonMinutes(parts) {
  return blocksMinutes(parts);
}

// Alle weeklessen uit de actieve seizoensplannen, als losse items. Lege lessen (geen
// titel en geen uitleg) worden overgeslagen.
export function planLessons(plans, data) {
  var out = [];
  (plans || []).forEach(function (p) {
    var weeks = data && data[p.id] ? data[p.id] : {};
    Object.keys(weeks).map(Number).sort(function (a, b) { return a - b; }).forEach(function (w) {
      var wd = weeks[w];
      if (!wd) return;
      ["lessonA", "lessonB"].forEach(function (lk, li) {
        var parts = wd[lk];
        var filled = parts && lessonBlocks(parts).some(function (b) { return hasText(parts[b.key]); });
        if (!filled) return;
        out.push({
          id: "plan:" + p.id + ":" + w + ":" + li,
          src: "plan", kind: "lesson", planId: p.id, week: w, les: li, level: p,
          color: colorKeyOf(p),
          name: wd.title || ("Week " + w),
          sub: wd.subtitle || "",
          theme: wd.theme || "", adjustments: wd.adjustments || "",
          tags: wd.tags || [],
          parts: parts,
        });
      });
    });
  });
  return out;
}

// ---------------------------------------------------------------- Firestore

function libCol(clubId) { return collection(db, "clubs", clubId, "library"); }

// Eigen items + gedeelde items van de club (twee queries, zodat de regels ze toestaan).
export async function loadLibrary(clubId, uid) {
  var res = await Promise.all([
    getDocs(query(libCol(clubId), where("ownerUid", "==", uid))),
    getDocs(query(libCol(clubId), where("shared", "==", true))),
  ]);
  var map = {};
  res.forEach(function (snap) {
    snap.forEach(function (d) { map[d.id] = Object.assign({ id: d.id }, d.data()); });
  });
  return Object.keys(map).map(function (k) { return map[k]; });
}

export function newLibId(clubId) { return doc(libCol(clubId)).id; }

// ---- Losse lessen uit het seizoensplan (centraal) ----
function offCol(planClubId) { return collection(db, "clubs", planClubId, "official"); }

export async function loadOfficial(planClubId) {
  var snap = await getDocs(offCol(planClubId));
  var out = [];
  snap.forEach(function (d) { out.push(officialItem(d.id, d.data())); });
  return out;
}
// Vult de velden aan die de bibliotheek gebruikt (bron, week, les).
export function officialItem(id, d) {
  var f = d.fromPlan || {};
  return Object.assign({}, d, { id: id, src: "plan", kind: "lesson", week: f.week, les: f.les, planLabel: f.label || "" });
}
export function officialId(planId, week, les) { return String(planId).replace(/[^A-Za-z0-9_-]/g, "_") + "_w" + week + "_" + (les ? "B" : "A"); }

// Een les uit een seizoensplan (zie planLessons) omzetten naar een losse les.
export function toOfficial(pl) {
  return {
    kind: "lesson", color: pl.color || "rood", name: pl.name || "", sub: pl.sub || "",
    theme: pl.theme || "", adjustments: pl.adjustments || "",
    tags: (pl.tags || []).slice(), parts: cp(pl.parts || mkLes()),
    fromPlan: { id: pl.planId, label: pl.level ? pl.level.label : "", week: pl.week, les: pl.les },
  };
}

// Kopieert alle ingevulde lessen van één seizoensplan. Een les die er al stond (zelfde
// plan, week en les A/B) wordt overschreven; zo kun je een plan na verbeteringen
// opnieuw kopiëren. Geeft de nieuwe items terug.
export async function copyPlanToOfficial(planClubId, planItems) {
  var out = [];
  for (var start = 0; start < planItems.length; start += 400) {
    var batch = writeBatch(db);
    planItems.slice(start, start + 400).forEach(function (pl) {
      var id = officialId(pl.planId, pl.week, pl.les);
      var data = cp(toOfficial(pl));
      data.copiedAt = serverTimestamp();
      batch.set(doc(db, "clubs", planClubId, "official", id), data);
      out.push(officialItem(id, toOfficial(pl)));
    });
    await batch.commit();
  }
  return out;
}

export async function saveOfficial(planClubId, item) {
  var data = cp(item);
  ["id", "src", "week", "les", "planLabel", "copiedAt", "updatedAt", "createdAt"].forEach(function (k) { delete data[k]; });
  data.updatedAt = serverTimestamp();
  await setDoc(doc(db, "clubs", planClubId, "official", item.id), data, { merge: true });
}
export async function deleteOfficial(planClubId, id) {
  await deleteDoc(doc(db, "clubs", planClubId, "official", id));
}

// Schrijft het item weg (zonder de lokale velden id/src/createdAt).
export async function saveLibItem(clubId, item, isNew) {
  var data = cp(item);
  delete data.id; delete data.src; delete data.createdAt; delete data.updatedAt;
  data.updatedAt = serverTimestamp();
  if (isNew) data.createdAt = serverTimestamp();
  await setDoc(doc(db, "clubs", clubId, "library", item.id), data, { merge: true });
}

export async function deleteLibItem(clubId, id) {
  await deleteDoc(doc(db, "clubs", clubId, "library", id));
}

export function mkLibLesson(owner, color) {
  return { kind: "lesson", ownerUid: owner.uid, ownerName: owner.name, shared: false, name: "", tags: [], color: color || "rood", parts: mkLes() };
}
export function mkLibDrill(owner) {
  return { kind: "drill", ownerUid: owner.uid, ownerName: owner.name, shared: false, name: "", tags: [], description: "", extraInfo: "", photo: "" };
}

// Kopie van een les (uit het seizoensplan of van een collega) als eigen, privé les.
export function copyAsLesson(owner, src) {
  var n = mkLibLesson(owner, src.color || "rood");
  n.name = src.name || "";
  n.tags = (src.tags || []).slice();
  n.parts = cp(src.parts || mkLes());
  n.copiedFrom = src.src === "plan" ? ("Seizoensplan" + (src.planLabel ? " " + src.planLabel : "") + (src.week ? ", week " + src.week + (src.les ? " les B" : " les A") : "")) : (src.ownerName || "");
  return n;
}
export function copyAsDrill(owner, src) {
  var n = mkLibDrill(owner);
  ["name", "description", "extraInfo", "photo", "diagram"].forEach(function (k) { if (src[k] !== undefined) n[k] = cp(src[k]); });
  n.tags = (src.tags || []).slice();
  n.copiedFrom = src.ownerName || "";
  return n;
}

// Alle tags die voorkomen in een lijst items, alfabetisch.
export function tagsOf(items) {
  var all = [];
  items.forEach(function (it) { (it.tags || []).forEach(function (t) { if (all.indexOf(t) < 0) all.push(t); }); });
  return all.sort(function (a, b) { return a.localeCompare(b, "nl"); });
}

export function initials(name) {
  var parts = String(name || "?").trim().split(/\s+/);
  return (parts[0].charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : "")).toUpperCase();
}
